/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColDef, GetRowIdParams, GridOptions, GridReadyEvent, RowSelectedEvent, RowSelectionOptions } from '@ag-grid-community/core';
import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AgFns,
  GridOptionsExt,
  ISortModel,
  PricedProductTypeFinderDialog,
  ProxAgFns,
  RowSelectionMultipleCheckbox,
  RowSelectionSingle,
  SaveAndQueryComponent,
  StatusService,
} from '@core';
import {
  Account,
  AccountIssuance,
  ActiveStatusEnum,
  PricedProductType,
  ProgramIssuance,
  ProgramProductCategoryTagMap,
  ProgramProductTypeTag,
  ProgramProductTypeTagMap,
} from '@models';
import { BaseService } from '@core';
import { UtilFns } from '@utils';
import * as _ from 'lodash';
import { AccountDbSaveService } from '../services/account-db-save.service';
import { AccountDbQueryService } from '../services/account-db-query.service';
import { EntityFns } from '@data';
import { ProgramProductTypePickerDialogComponent } from '../programs/program-product-type-picker.dialog.component';
import { ConsolidateTagsDialogComponent } from './consolidate-tags.dialog.component';

@Component({
  selector: 'prox-program-product-type-tags',
  templateUrl: './program-product-type-tags.component.html',
  standalone: false,
})
export class ProgramProductTypeTagsComponent extends SaveAndQueryComponent {
  accountId!: string;
  account!: Account;
  tagGridOptions!: GridOptionsExt;
  tags?: ProgramProductTypeTag[];
  selectedTag?: ProgramProductTypeTag;
  public tagRowSelection: RowSelectionOptions = RowSelectionMultipleCheckbox;
  selectedTagCount = 0;

  tmapGridOptions!: GridOptionsExt;
  tmaps: ProgramProductTypeTagMap[] = [];

  pptGridOptions!: GridOptionsExt;
  allPpts: PricedProductType[] = [];
  ppts: PricedProductType[] = [];
  pptSelectedCount = 0;
  shouldShowOnlyProductsWithNoTags = false;

  tabViewIndex = 0;

  constructor(
    baseService: BaseService,
    route: ActivatedRoute,
    private statusService: StatusService,
    override dbSaveService: AccountDbSaveService,
    override dbQueryService: AccountDbQueryService,
  ) {
    super(baseService, route, dbSaveService, dbQueryService);
  }

  override async updateFromParams(params: object): Promise<void> {
    this.accountId = params['accountId'];
    UtilFns.assertNonEmptyString(this.accountId, 'accountId');
    this.account = await this.dbQueryService.getAccountById(this.accountId);
    UtilFns.assertNonNull(this.account, 'Account');

    this.tagGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onTagGridReady,
      onRowSelected: this.onTagRowSelected,
      rowSelection: RowSelectionMultipleCheckbox,
      getRowId: (rowIdParams: GetRowIdParams) => {
        const x = rowIdParams.data as ProgramProductTypeTag;
        return x.id;
      },
    });
    AgFns.captureGridRouteParams(this.tagGridOptions, this.route, 'id');

    this.tmapGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onTmapGridReady,
      getRowId: (rowIdParams: GetRowIdParams) => {
        const x = rowIdParams.data as ProgramProductTypeTagMap;
        return x.pricedProductTypeId;
      },
    });

    this.pptGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onPptGridReady,
      onRowSelected: this.onPptRowSelected,
      rowSelection: RowSelectionMultipleCheckbox,
      getRowId: (rowIdParams: GetRowIdParams) => {
        const x = rowIdParams.data as PricedProductType;
        return x.id;
      },
    });

    this.tags = await this.dbQueryService.getProgramProductTypeTagsForAccount(this.accountId);
    this.allPpts = await this.dbQueryService.getPricedProductTypesForAccount(this.accountId);
    this.refreshPpts();

    this.setTitle('Account Product Tags for ' + this.account.name);

    this.isPageReady = true;
  }

  isReadOnly() {
    return this.statusService.getWorkingStatus(this.account).isActiveReadOnly;
  }

  canDelete() {
    return !this.isReadOnly();
  }

  onTabChange(event: any) {
    if (event.index == 0) {
      this.tmaps = this.selectedTag?.programProductTypeTagMaps ?? [];
      AgFns.refreshGrid(this.tmapGridOptions, this.tmaps);
      // AgFns.sizeColumnsToFit(this.pptGridOptions);
    }
  }

  statusMessage() {
    let msg = '';
    if (this.account?.activeStatusId == ActiveStatusEnum.Active) {
      return 'Account is Active and Not Editable.  To edit, set Account status to Hold.';
    } else if (this.account?.activeStatusId == ActiveStatusEnum.Hold) {
      msg = 'Account is On-Hold and is Editable';
    } else {
      return 'Account is ' + this.account?.activeStatus.name + ' and is Not Editable';
    }
    return msg;
  }

  // Tag Grid

  onTagGridReady(event: GridReadyEvent) {
    const gridOptions = event.context.gridOptions as GridOptions;
    const [colDefs, sortModel] = this.getTagColDefsAndSortModel();
    AgFns.initGrid(event, colDefs, sortModel);
    AgFns.applyGridRouteParams(gridOptions);
  }

  getTagColDefsAndSortModel() {
    const colDefs: ColDef[] = [
      ProxAgFns.getEntityStateColDef(),
      { headerName: 'Name', field: 'name', editable: !this.isReadOnly(), filter: 'agTextColumnFilter' },

      ProxAgFns.getEntityDeleteColDef(this.onTagDelete.bind(this), { canDisplay: this.canDelete.bind(this) }),
    ];
    const sortModel: ISortModel = [{ colId: 'name', sort: 'asc' }];
    return [colDefs, sortModel] as const;
  }

  async onTagRowSelected(event: RowSelectedEvent) {
    const selectedRows = (this.tagGridOptions.apiX?.getSelectedRows() ?? []) as ProgramProductTypeTag[];
    this.selectedTagCount = selectedRows.length;

    // Only load the per-tag product-type list when exactly one tag row is selected. With
    // multi-select enabled, the user is choosing a consolidation set — the lower grid stays
    // empty until they narrow back to one.
    if (selectedRows.length !== 1) {
      this.selectedTag = undefined;
      this.tmaps = [];
      AgFns.refreshGrid(this.tmapGridOptions, this.tmaps);
      return;
    }

    if (!event.node.isSelected()) return;
    const tag = selectedRows[0];
    this.selectedTag = tag;
    if (tag == null) return;

    if (tag.entityAspect.entityState.isDeleted()) {
      this.selectedTag = undefined;
      this.tagGridOptions.apiX?.deselectAll();
      return;
    }
    if (tag.entityAspect.isNavigationPropertyLoaded('programProductTypeTagMaps')) {
      this.tmaps = tag.programProductTypeTagMaps;
    } else {
      this.tmaps = await this.dbQueryService.getProgramProductTypeTagMapsForTag(tag.id);
    }

    AgFns.refreshGrid(this.tmapGridOptions, this.tmaps);
    this.updateGridRouteParams(this.tagGridOptions, tag.id);
  }

  getSelectedTags(): ProgramProductTypeTag[] {
    return (this.tagGridOptions.apiX?.getSelectedRows() ?? []) as ProgramProductTypeTag[];
  }

  canConsolidate(): boolean {
    return !this.isReadOnly() && this.selectedTagCount >= 2;
  }

  async onConsolidateSelected() {
    if (this.dbSaveService.hasChanges()) {
      this.toastr.warning('Please save or cancel your changes before consolidating tags.');
      return;
    }
    const selected = this.getSelectedTags().filter((t) => !t.entityAspect.entityState.isAdded());
    if (selected.length < 2) {
      this.toastr.warning('Select at least two saved tags to consolidate.');
      return;
    }

    const result = await ConsolidateTagsDialogComponent.openDialog(this.dialogService.pngDialogService, {
      selectedTags: selected,
    });

    if (!result?.consolidated) return;

    // Server has hard-deleted the synonym tag rows. Refresh the tag list from the server
    // (Breeze cache is now out of sync with deleted rows) and reset selection state.
    this.selectedTag = undefined;
    this.selectedTagCount = 0;
    this.tmaps = [];
    this.tags = await this.dbQueryService.getProgramProductTypeTagsForAccount(this.accountId);
    this.tagGridOptions.apiX?.deselectAll();
    AgFns.refreshGrid(this.tagGridOptions, this.tags);
    AgFns.refreshGrid(this.tmapGridOptions, this.tmaps);
  }

  async onTagAdd() {
    const tag = this.dbSaveService.createEntity(ProgramProductTypeTag, { accountId: this.accountId });
    tag.entityAspect.validateEntity();
    this.tags?.push(tag);
    this.tmaps = [];
    AgFns.refreshGrid(this.tagGridOptions, this.tags);
    AgFns.refreshGrid(this.tmapGridOptions, this.tmaps);
    AgFns.selectGridRowByKey(this.tagGridOptions, (e: ProgramProductTypeTag) => e.id, tag.id, 'name');
    this.tabViewIndex = 0;
  }

  async onTagDelete(tag: ProgramProductTypeTag) {
    // Check if form has unsaved changes
    if (this.dbSaveService.hasChanges()) {
      this.toastr.warning('Please save or cancel your changes before deleting a product type tag.');
      return;
    }

    // Check if tag is in use
    const inUse = await this.checkIfTagInUse(tag);
    if (inUse) {
      return;
    }

    // Confirm delete
    const ok = await this.dialogService.askIfOk('Delete Product Type Tag', `Are you sure you want to delete the tag "${tag.name}"?`);
    if (!ok) return;

    // Delete related entities
    // if it's an added record - cascade delete won't catch them.
    if (tag.entityAspect.entityState.isAdded()) {
      tag.programProductTypeTagMaps.slice().forEach((x) => EntityFns.deleteOrDetach(x.entityAspect));
    }
    EntityFns.deleteOrDetach(tag.entityAspect);

    // Atomic save
    try {
      await this.dbSaveService.saveChanges();
      this.toastr.success('Product type tag deleted successfully.');

      // Update UI
      this.selectedTag = undefined;
      this.tmaps = [];
      if (this.tags) {
        _.remove(this.tags, tag);
        AgFns.refreshGrid(this.tagGridOptions, this.tags);
      }
    } catch (error) {
      this.toastr.error('Failed to delete product type tag.');
      throw error;
    }
  }

  async checkIfTagInUse(tag: ProgramProductTypeTag, skipPpttMapCheck = false): Promise<boolean> {
    const msgTitle = 'Cannot delete';
    const msg = skipPpttMapCheck
      ? `You cannot delete the last Product Type on a tag that has `
      : `You cannot delete a Product Type Tag that has`;

    const pis = await this.dbQueryService.findInUse(tag, ProgramIssuance, 'programProductTypeTagId', ['program']);
    if (pis.length > 0) {
      const names = pis
        .map((x) => {
          return `Program: ${x.program.name} / Issuance: ${x.name}`;
        })
        .join('<br/>');
      await this.dialogService.warnDialog(msgTitle, `${msg} Program Issuances associated with it.<br/><br/> ${names}`);
      return true;
    }

    const ais = await this.dbQueryService.findInUse(tag, AccountIssuance, 'programProductTypeTagId', ['account']);
    if (ais.length > 0) {
      const names = ais
        .map((x) => {
          return `Account: ${x.account.name} / Issuance: ${x.name}`;
        })
        .join('<br/>');
      await this.dialogService.warnDialog(msgTitle, `${msg} Account Issuances associated with it.<br/><br/> ${names}`);
      return true;
    }

    const ppctms = await this.dbQueryService.findInUse(tag, ProgramProductCategoryTagMap, 'programProductTypeTagId', [
      'programProductCategoryTag.program',
    ]);
    if (ppctms.length > 0) {
      const names = ppctms
        .map((x) => {
          const ppct = x.programProductCategoryTag;
          const name = `Program: ${ppct.program.name} / Menu: ${ppct.name}`;
          return name;
        })
        .join('<br/>');
      await this.dialogService.warnDialog(msgTitle, `${msg} Menu categories associated with it.<br/><br/> ${names}`);
      return true;
    }

    if (skipPpttMapCheck) return false;

    const pptms = await this.dbQueryService.findInUse(tag, ProgramProductTypeTagMap, 'programProductTypeTagId', [
      'pricedProductType.productType.manufacturer',
    ]);
    if (pptms.length > 0) {
      const names = pptms
        .map((x) => {
          const ppt = x.pricedProductType.productType;
          const fullName = ppt.manufacturer.name + ' ' + ppt.name + ' ' + (ppt.productKey ?? '');
          return fullName;
        })
        .join('<br/>');
      await this.dialogService.warnDialog(
        msgTitle,
        `You cannot delete a Product Type Tag that has Priced Product Types associated with it.<br/><br/> ${names}`,
      );
      return true;
    }

    return false;
  }

  async checkIfStranded(ppttm: ProgramProductTypeTagMap, currentPptms: ProgramProductTypeTagMap[]): Promise<boolean> {
    return AgFns.busyGrid(this.tmapGridOptions, this, async () => {
      const toast = this.toastr.info('Checking issuance limit coverage across programs...', 'Please wait', {
        disableTimeOut: true,
      });
      try {
        return await this.checkIfStrandedCore(ppttm, currentPptms);
      } finally {
        this.toastr.clear(toast.toastId);
      }
    });
  }

  async checkIfStrandedCore(ppttm: ProgramProductTypeTagMap, currentPptms: ProgramProductTypeTagMap[]): Promise<boolean> {
    // Server-side issuance coverage check. Replaces the old client-side deep-expand query
    // with a single lightweight API call. See Docs/issuance-limit-logic.md for the rule.
    const affectedPrograms = await this.dbQueryService.checkIssuanceCoverage(ppttm.pricedProductTypeId, ppttm.programProductTypeTagId);

    if (affectedPrograms.length > 0) {
      const programNames = affectedPrograms.map((x) => x.Name).join('<br/>');
      const ok = await this.dialogService.askIfOk(
        'Confirm Hold',
        `Detaching this Product Type from the '${ppttm.programProductTypeTag.name}' tag affects issuance limit coverage in the following programs:
        <br/><br/>${programNames}.<br/><br/>These programs will be put On Hold so you can proceed. You must revalidate and reactivate them afterward.`,
      );
      if (!ok) return true;

      // Load each affected program into the Breeze cache and set to Hold
      for (const ap of affectedPrograms) {
        const program = await this.dbQueryService.getProgramByIdSimple(ap.Id);
        program.activeStatusId = ActiveStatusEnum.Hold;
      }
    }
    return false;
  }

  // ------------- Tmap grid --------------------------

  onTmapGridReady(event: GridReadyEvent) {
    const [colDefs, sortModel] = this.getTmapColDefsAndSortModel();
    AgFns.initGrid(event, colDefs, sortModel);
  }

  getTmapColDefsAndSortModel() {
    const ptypePath = 'pricedProductType.productType';
    const colDefs: ColDef[] = [
      ProxAgFns.getEntityStateColDef(),
      { ...ProxAgFns.getSupplierColDef(ptypePath + '.supplier.name'), ...AgFns.flexWidth(150) },
      { ...ProxAgFns.getManufacturerColDef(ptypePath + '.manufacturer.name'), ...AgFns.flexWidth(150) },
      { ...ProxAgFns.getProductTypeNameColDef(ptypePath + '.name'), ...AgFns.flexWidth(100) },
      {
        ...ProxAgFns.getProductTypeDescriptionColDef(ptypePath + '.description'),
        headerName: 'Description',
        tooltipValueGetter: (params) => params.data?.pricedProductType?.productType?.description,
        ...AgFns.flexWidth(300),
      },

      ProxAgFns.getEntityDeleteColDef(this.onTmapDelete.bind(this), { canDisplay: this.canDelete.bind(this) }),
    ];
    const sortModel: ISortModel = [
      { colId: ptypePath + '.supplier.name', sort: 'asc' },
      { colId: ptypePath + '.manufacturer.name', sort: 'asc' },
      { colId: ptypePath + '.name', sort: 'asc' },
    ];
    return [colDefs, sortModel] as const;
  }

  async onTmapAdd() {
    if (!this.selectedTag) return;
    const tagId = this.selectedTag.id;
    const queryFn = () => this.dbQueryService.createPricedProductTypesQuery(this.accountId, tagId);
    const pricedProductTypes = await this.dialogService.createFinder(PricedProductTypeFinderDialog, {
      queryFn,
      rowSelection: RowSelectionMultipleCheckbox,
    });
    const newPpts = pricedProductTypes.filter((ppt) => !ppt.programProductTypeTagMaps.some((x) => x.programProductTypeTagId == tagId));
    const newTmaps = newPpts.map((ppt) => {
      let tmap = this.dbSaveService.uow.undoIfDeleted(ProgramProductTypeTagMap, [ppt.id, tagId]);
      if (tmap == null) {
        tmap = this.dbSaveService.createEntity(ProgramProductTypeTagMap, {
          pricedProductTypeId: ppt.id,
          programProductTypeTagId: tagId,
        });
      }
      return tmap;
    });

    this.tmaps.push(...newTmaps);
    AgFns.refreshGrid(this.tmapGridOptions, this.tmaps);
  }

  async onTmapDelete(tmap: ProgramProductTypeTagMap) {
    if (await this.checkIfStranded(tmap, this.tmaps)) return;

    if (this.tmaps.length == 1) {
      const inUse = await this.checkIfTagInUse(tmap.programProductTypeTag, true);
      if (inUse) {
        return;
      }
    }

    EntityFns.deleteOrDetach(tmap.entityAspect);
    _.remove(this.tmaps, tmap);
    AgFns.refreshGrid(this.tmapGridOptions, this.tmaps);
  }

  // ------------- Ppt grid --------------------------

  onPptGridReady(event: GridReadyEvent) {
    const [colDefs, sortModel] = this.getPptColDefsAndSortModel();
    AgFns.initGrid(event, colDefs, sortModel);
  }

  getPptColDefsAndSortModel() {
    const colDefs: ColDef[] = [
      { ...ProxAgFns.getSupplierColDef('productType.supplier.name'), ...AgFns.flexWidth(100) },
      { ...ProxAgFns.getManufacturerColDef('productType.manufacturer.name'), ...AgFns.flexWidth(100) },
      { ...ProxAgFns.getProductTypeNameColDef('productType.name'), ...AgFns.flexWidth(150) },
      {
        ...ProxAgFns.getProductTypeDescriptionColDef('productType.description'),
        headerName: 'Description',
        tooltipValueGetter: (params) => params.data?.productType?.description,
      },

      {
        ...AgFns.createButtonProps('Tags', this.onTags.bind(this), {
          calcLabel: this.calcTagsLabel.bind(this),
          calcDisabled: () => this.isReadOnly(),
        }),
        ...AgFns.flexWidth(100),
      },
    ];
    const sortModel: ISortModel = [
      { colId: 'productType.supplier.name', sort: 'asc' },
      { colId: 'productType.manufacturer.name', sort: 'asc' },
      { colId: 'productType.name', sort: 'asc' },
    ];
    return [colDefs, sortModel] as const;
  }

  async onPptRowSelected(event: RowSelectedEvent) {
    this.pptSelectedCount = <number>this.pptGridOptions.apiX?.getSelectedRows().length;
    if (!event.node.isSelected()) return;
  }

  getSelectedPpts() {
    return this.pptGridOptions.apiX?.getSelectedRows() || [];
  }

  refreshPpts() {
    if (this.shouldShowOnlyProductsWithNoTags) {
      this.ppts = this.allPpts.filter((x) => x.programProductTypeTagsDescr == '');
    } else {
      this.ppts = this.allPpts;
    }
  }

  calcTagsLabel(ppt: PricedProductType) {
    return ppt.programProductTypeTagsDescr == '' ? ' - NONE - ' : ppt.programProductTypeTagsDescr;
  }

  async onTags(ppt: PricedProductType) {
    await this.dialogService.createFinder(ProgramProductTypePickerDialogComponent, {
      tags: this.tags || [],
      parents: [ppt],
      rowSelection: RowSelectionMultipleCheckbox,
      checkIfStranded: this.checkIfStranded.bind(this),
    });
  }

  async onMultiTags() {
    const selectedNodes = this.pptGridOptions.apiX?.getSelectedNodes();
    if (!selectedNodes || !selectedNodes.length) {
      return;
    }

    const pricedProductTypes = selectedNodes.map((x) => x.data as PricedProductType).filter((x) => !!x) as any[];

    await this.dialogService.createFinder(ProgramProductTypePickerDialogComponent, {
      tags: this.tags || [],
      parents: pricedProductTypes,
      rowSelection: RowSelectionMultipleCheckbox,
      checkIfStranded: this.checkIfStranded.bind(this),
    });
    this.refreshPpts();
  }

  // ---- Misc ----------------------------

  override async addCrossValidationErrors() {
    EntityFns.checkForDupErrors(
      this.tags,
      (x) => x.name,
      (e, dupName) => this.createValidationError(e, 'name', `This Product Tag Name is a duplicate: '${dupName}.`),
    );
  }

  override async afterSave() {
    return this.afterUndo();
  }

  override async afterUndo() {
    if (this.tags) {
      this.tags = await this.dbQueryService.getProgramProductTypeTagsForAccount(this.accountId);
      this.tmaps = [];
    }
    if (!this.tagGridOptions.apiX) return;
    this.tagGridOptions.apiX?.deselectAll();

    await AgFns.refreshGrid(this.tagGridOptions, this.tags);
    AgFns.selectFirstRow(this.tagGridOptions);
  }
}
